/**
 * Single LEARNSET row primitive (DR-9 + DR-4, 2026-05-13).
 *
 * Three variants surface different chrome:
 *
 *   'already-learned' — Move the Pokémon currently knows OR knew at/below
 *                       its current level. Renders at full opacity.
 *   'will-learn'      — Move the species learns at a future level. Renders
 *                       at reduced contrast (text-secondary).
 *   'replaced'        — Move the Pokémon SHOULD have learned per the species
 *                       curve at/below its level but no longer has in moves[]
 *                       (level-up replaced via the LearnedMovesModal). Renders
 *                       with strike-through + "replaced" caption per DR-9.
 *
 * Plus an EGG MOVE section heading helper (DR-4 — section label,
 * NOT ✨ emoji per DESIGN.md anti-slop).
 *
 * Pure DOM. No Phaser dependency. Caller (PR 3 FullSheet LEARNSET tab)
 * parents into the appropriate section container; teardown happens via
 * FullSheet's root.remove().
 *
 * Styling lives in `apps/game/src/styles/tokens.css` under
 * `.learnset-row` + modifiers.
 */

export type LearnsetRowVariant = 'already-learned' | 'will-learn' | 'replaced';

export interface LearnsetRowOptions {
  /** Level at which the move is learned. Rendered as "L7" / "L23" etc. */
  level: number;
  /** Display name ("Tackle", "Vine Whip"). Title-cased upstream. */
  moveName: string;
  /** Variant — drives styling + the strike-through / "replaced" caption. */
  variant: LearnsetRowVariant;
  /** Optional type chip on the row's trailing edge ("Grass", "Fire"). Case-
   *  insensitive; resolves to a `--type-*` token. When omitted, no chip. */
  type?: string;
}

/**
 * Build a single `.learnset-row` element. Returned node is an unparented
 * `<li>` — caller appends to a `<ul class="learnset-section">` container.
 */
export function createLearnsetRow(opts: LearnsetRowOptions): HTMLElement {
  const el = document.createElement('li');
  el.className = `learnset-row learnset-row--${opts.variant}`;

  const lvl = document.createElement('span');
  lvl.className = 'learnset-row__level';
  lvl.textContent = `L${opts.level}`;
  el.appendChild(lvl);

  const name = document.createElement('span');
  name.className = 'learnset-row__name';
  name.textContent = opts.moveName;
  el.appendChild(name);

  if (opts.variant === 'replaced') {
    const cap = document.createElement('span');
    cap.className = 'learnset-row__caption';
    cap.textContent = 'replaced';
    el.appendChild(cap);
  }

  if (opts.type) {
    const typ = document.createElement('span');
    typ.className = 'learnset-row__type';
    typ.style.setProperty('--row-type', `var(--type-${opts.type.toLowerCase()})`);
    typ.textContent = opts.type;
    el.appendChild(typ);
  }

  return el;
}

/**
 * Build the "EGG MOVE" section heading (DR-4 — replaces ✨ emoji).
 * Rendered above the egg-move row in LEARNSET. Returns an unparented
 * `<div>`; caller positions it in the section flow.
 */
export function createEggMoveSectionLabel(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'learnset-section-label';
  el.textContent = 'EGG MOVE';
  return el;
}
